import { useEffect, useRef } from "react";
import { AsynchronousExecutionStatusGuidance } from "./asynchronous-execution-ui";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "../app-bindings/attachment-state-ui";
import { useAsynchronousExecutionDetail, useAsynchronousExecutions, type AsynchronousExecutionDetailResponse } from "../app-bindings/hooks/useAppBindings";
import type { AutomaticAlignmentState } from "../app-bindings/hooks/useAutomaticAttachmentAlignment";

export function AutomaticAlignmentProgress({ execution }: { execution: AsynchronousExecutionDetailResponse }) {
  const progress = execution.automaticAlignmentProgress;
  const position = (value: NonNullable<typeof progress>["processingPosition"]) => value
    ? `${formatDateTime(value.updatedAt)} / レコードID ${value.recordId}` : "なし";
  return <section aria-label="自動整合化の処理状況" className="space-y-2 text-sm">
    <AsynchronousExecutionStatusGuidance execution={execution} />
    {progress ? <>
      <p>{automaticAlignmentPhaseDescription(execution.executionState, progress.phase)}</p>
      <p>今回の対象：{progress.selectedRecordCount}件 / 処理済み：{progress.processedRecordCount}件</p>
      <p>今回の処理位置：{position(progress.processingPosition)}</p>
      <p>この実行の確定再開位置：{progress.committedPosition ? position(progress.committedPosition) : "先頭から"}</p>
      <p className="text-muted-foreground">処理位置と確定再開位置は異なる場合があります。直近の更新は次回も確認するため、処理しても確定再開位置を進めません。現在のアプリの位置は自動整合化設定で確認できます。</p>
    </> : <p>処理件数と確認位置は未確認です。状態を再取得してください。</p>}
  </section>;
}

function automaticAlignmentPhaseDescription(state: AsynchronousExecutionDetailResponse["executionState"],
  phase: NonNullable<AsynchronousExecutionDetailResponse["automaticAlignmentProgress"]>["phase"]) {
  if (state === "completed") return "今回の選択範囲の整合化が完了しました。";
  if (state === "failed" || state === "canceled") return "この実行の選択・整合化処理は終了しています。";
  if (state === "cancel_requested") return "この実行の選択・整合化処理は取消処理中です。";
  const selecting = phase === "selecting";
  if (state === "queued") return selecting ? "今回の対象レコードの選択は待機中です。" : "選択済みのレコードの整合化は再開待ちです。";
  if (state === "blocked") return selecting ? "今回の対象レコードの選択は要対応で停止しています。" : "選択済みのレコードの整合化は要対応で停止しています。";
  return selecting ? "今回の対象レコードを選択しています。" : "選択済みのレコードを整合化しています。";
}

/** The current root takes precedence; retained automatic results remain visible
 * after settlement even when recent records did not advance the app pointer. */
export function AutomaticAlignmentProgressSummary({ state }: { state: AutomaticAlignmentState }) {
  const currentId = state.activeExecution?.alignmentOrigin === "automatic" ? state.activeExecution.executionId : "";
  const history = useAsynchronousExecutions({ kintoneAppId: state.kintoneAppId, executionKind: "attachment_storage_alignment", alignmentOrigin: "automatic", executionState: "completed", limit: "1" });
  const latest = history.items.find(item => item.appBindingId === state.appBindingId && item.alignmentOrigin === "automatic");
  const detail = useAsynchronousExecutionDetail(currentId || latest?.executionId || "", true);
  const previous = useRef(currentId);
  const refreshHistory = history.refresh;
  useEffect(() => {
    if (previous.current && !currentId) void refreshHistory();
    previous.current = currentId;
  }, [currentId, refreshHistory]);
  const execution = detail.execution?.appBindingId === state.appBindingId && detail.execution?.alignmentOrigin === "automatic" && detail.execution?.executionId === (currentId || latest?.executionId) ? detail.execution : null;
  const error = detail.error || (!currentId && history.error);
  return <div className="space-y-2 rounded-md border border-border p-3" aria-label="自動整合化の実行結果">
    <p className="font-medium">{currentId ? "現在の自動整合化" : "直近の完了した自動整合化"}</p>
    {error ? <p>進捗の取得に失敗しました。{error}{execution ? " 表示は最終取得時の状態です。" : ""}</p> : null}
    {execution ? <><AutomaticAlignmentProgress execution={execution} /><Link className="block underline" to={`/console/asynchronous-executions/${execution.executionId}?kintoneAppId=${encodeURIComponent(state.kintoneAppId)}`}>自動整合化の実行詳細を見る</Link></>
      : <p>{history.isLoading || detail.isLoading ? "処理件数と確認位置を確認しています。" : error ? "処理件数と確認位置は未確認です。" : currentId ? "現在の処理状況を確認できません。" : "完了した自動整合化はありません。"}</p>}
    {error || execution && !execution.automaticAlignmentProgress ? <Button type="button" variant="outline" size="sm" onClick={() => { void refreshHistory(); void detail.refresh(); }}>自動整合化の進捗を再取得</Button> : null}
  </div>;
}
